import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Coins, X, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";

const PRESETS = [25, 50, 100, 250, 500, 1000];

export default function TipModal({ open, onClose, streamerUsername }) {
  const { user, refreshMe } = useAuth();
  const [amount, setAmount] = useState(50);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [recipient, setRecipient] = useState(null);

  useEffect(() => {
    if (!open || !streamerUsername) return;
    setErr(""); setDone(false);
    // Load recipient's verified status
    api.get(`/users/${streamerUsername}/public`).then(r => setRecipient(r.data)).catch(() => setRecipient({}));
  }, [open, streamerUsername]);

  if (!open) return null;

  const goldBal = user?.gold_coins ?? 0;
  const recipientVerified = recipient?.is_verified_streamer !== false; // treat unknown as allowed to try
  const numAmt = Number(amount) || 0;
  const insufficient = numAmt > goldBal;

  const send = async () => {
    setSending(true); setErr("");
    try {
      await api.post("/tips", { streamer_username: streamerUsername, amount: Number(amount), message });
      setDone(true);
      refreshMe();
      setTimeout(() => { setDone(false); onClose(); }, 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Tip failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(20px)" }} data-testid="tip-modal">
      <div className="glass max-w-md w-full p-7 rounded-md relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-white" data-testid="tip-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Coins className="w-5 h-5 text-[#FFD700]" />
          <div className="sc-label" style={{ color: "#FFD700" }}>Tip in Gold Coins</div>
        </div>
        <h2 className="sc-heading text-2xl font-bold mb-1">Send a tip to {streamerUsername}</h2>
        <p className="text-xs text-zinc-400 mb-5">100% of the tip goes to the streamer. Only verified streamers can receive tips.</p>

        {!recipientVerified ? (
          <div className="p-4 border border-[color:var(--sc-orange)]/40 bg-[color:var(--sc-orange)]/5 rounded-sm">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-5 h-5 text-[color:var(--sc-orange)] mt-0.5" />
              <div>
                <div className="text-sm font-semibold">Only verified streamers can receive tips</div>
                <div className="text-xs text-zinc-400 mt-1">{streamerUsername} has not completed streamer verification yet, so tipping is disabled for compliance reasons.</div>
              </div>
            </div>
          </div>
        ) : user ? (
          <>
            <div className="text-xs text-zinc-400 mb-3">Your balance: <span className="font-bold text-[#FFD700]" data-testid="tip-balance">{goldBal} Gold Coins</span> · <Link to="/credits" className="text-[color:var(--sc-cyan)] hover:underline">Buy more</Link></div>
            <label className="sc-label block mb-2">Amount</label>
            <div className="flex gap-2 flex-wrap mb-3">
              {PRESETS.map(p => (
                <button key={p} data-testid={`tip-preset-${p}`} onClick={() => setAmount(p)} className={`px-3 py-1.5 text-xs rounded-sm border ${amount === p ? "border-[#FFD700] text-[#FFD700] bg-[#FFD700]/10" : "border-white/10 hover:border-white/30"}`}>
                  {p}
                </button>
              ))}
            </div>
            <input data-testid="tip-amount" type="number" min={1} value={amount} onChange={e => setAmount(e.target.value)} className="sc-input mb-3" />
            <label className="sc-label block mb-2">Message (optional)</label>
            <input data-testid="tip-message" value={message} onChange={e => setMessage(e.target.value)} maxLength={200} className="sc-input mb-4" placeholder="GG!" />

            {err && <div className="text-[#FF3366] text-xs mb-3">{err}</div>}
            {insufficient && <div className="text-[color:var(--sc-orange)] text-xs mb-3">Not enough Gold Coins. <Link to="/credits" className="underline">Buy more</Link>.</div>}
            {done && <div className="text-[#10B981] text-sm mb-3 font-bold">Tip sent!</div>}

            <div className="flex gap-2">
              <button data-testid="tip-send" disabled={sending || done || insufficient || numAmt <= 0} onClick={send} className="sc-btn flex-1">{sending ? "Sending..." : `Send ${amount} Gold Coins`}</button>
              <Link to="/credits" className="sc-btn-ghost text-sm">Buy</Link>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="text-sm text-zinc-300 mb-4">Log in to tip your favorite streamers.</div>
            <Link to="/login" className="sc-btn inline-block">Log in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
